<template>
  <div v-if="isRoleAllowed">
    <b-spinner
      v-if="isFetching"
      small
      variant="secondary"
      class="ml-2"
    />
    <b-alert
      v-else-if="!getStakeholder"
      :show="showInfoAlert"
      variant="warning"
      class="mt-3 w-100 create-process"
    >
      <div>{{ $t(`${translationPath}.stakeholderNotFound`, { name: municipalityName }) }}</div>
    </b-alert>
    <div
      v-else
      class="mt-4"
    >
      <div>
        <b-alert
          :show="showInfoAlert"
          variant="primary"
          class="mt-3 w-100 create-process"
        >
          <ul class="p-1">
            {{ $t(`${translationPath}.requirements`) }}
            <li
              v-if="!hasStatusToStartProcess"
              class="mt-2"
            >
              {{ $t(`${translationPath}.statusTypes`) }}
              <ul class="pl-4">
                <li class="mt-1">
                  {{ $t('groupLabels.suggestion') }}
                  <img
                    width="15"
                    :src="image({ name: suggestion })"
                    class="mx-1"
                  >
                </li>
                <li class="mt-1">
                  {{ $t('groupLabels.definitive') }}
                  <img
                    width="15"
                    :src="image({ name: definitive })"
                    class="mx-1"
                  >
                </li>
              </ul>
            </li>
            <li
              v-if="!isCorrectCpoSelected"
              class="mt-1"
            >
              {{ differentUserCpo ? $t(`${translationPath}.differentUserCpo`) : $t(`${translationPath}.selectedCpo`) }}
            </li>
          </ul>
        </b-alert>
        <div v-if="(!isInProcess && getStakeholder.tenants.length)">
          <div
            v-if="canSelectCpo"
          >
            <strong>{{ $t(`${translationPath}.selectCpo`) }}</strong>
            <b-form-select
              v-model="selectedCpo"
              :options="selectableCpos"
              :disabled="isLoading"
              class="my-2"
            />
          </div>
          <div
            v-if="canSelectTenant"
          >
            <strong>{{ $t(`${translationPath}.selectTenant`) }}</strong>
            <b-form-select
              v-model="selectedCpoTenant"
              :options="selectableTenants"
              :disabled="isLoading || !selectedCpo"
              class="my-2"
            />
          </div>
          <div v-if="canSelectCategories">
            <strong>{{ $t(`${translationPath}.selectProcessCategory`) }}</strong>
            <b-form-select
              v-model="processCategory"
              :options="processCategories"
              :disabled="isCategorySelectDisabled"
              class="my-2"
            />
            <div
              v-if="selectedCpo || selectedTenant"
              class="my-2"
            >
              <strong>{{ $t('preOrselected') }}:</strong>
              <div>Workflow: {{ tenantName }}</div>
              <div>CPO: {{ selectedCpo ? selectedCpo.name : '' }}</div>
              <div>{{ $t(`${translationPath}.processCategory`) }}: {{ categoryName }}</div>
            </div>
            <b-button
              :disabled="!processCategory"
              variant="primary"
              class="mt-3 w-100 create-process"
              @click="startWorkflowProcess"
            >
              <b-spinner
                v-if="isLoading"
                small
              />
              <span>{{ $t(`${translationPath}.createProcess`) }}</span>
            </b-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import chargingpointDataMixin from '@/mixins/chargingpoint/chargingpointDataMixin'
import chargingpointEditMixin from '@/mixins/chargingpoint/chargingpointEditMixin'
import { image } from '@/helpers/assets'

export default {
  name: 'CreateWorkflowProcess',
  mixins: [chargingpointDataMixin, chargingpointEditMixin],
  data () {
    return {
      isLoading: false,
      isFetching: true,
      hasWorkflowTenant: false,
      allowedMunicipalityRoles: ['admin', 'municipality'],
      allowedCpoRoles: ['admin', 'cpo'],
      translationPath: 'components.sidebar.Tabs.ProcessInfoTab',

      image,
      definitive: 'chargingpoint/png/definitive.png',
      suggestion: 'chargingpoint/png/suggestion.png',
      processCategory: null,
      processCategories: null,
      selectableCpos: [],
      selectedCpo: null,
      selectedCpoTenant: null,
      tenantName: '',
      isMultiTenantCpo: false,
      selectableTenants: [],
      selectedTenant: null,
      activeTenants: [],
      activeCpos: [],
      municipalityName: '',
    }
  },
  computed: {
    ...mapGetters('access', ['getActiveMunicipality', 'getWorkflowTenant', 'getStakeholder', 'getAllOptionsCPO']),
    ...mapGetters('config', ['config']),
    ...mapGetters('currentUser', ['getCurrentUserRole']),

    isInProcess() {
      return (
        !!this.chargingpoint?.workflowCaseRef ||
        !!this.chargingpoint?.externalProcessId
      )
    },
    workflowRole () {
      const tenantRole = this.$auth.user['https://evmaps.nl/workflow'].roles.find(role => role.tenant === this.selectedTenant)
      if (!this.selectedTenant || !tenantRole) return {}

      const role = Object.keys(tenantRole).filter(key => key !== 'tenant')?.[0]
      return { role, value: tenantRole?.[role] }
    },
    canSelectCpo () {
      if (this.isUserCpo && this.differentUserCpo) return false

      return this.selectableCpos.length > 1 && (this.hasUserRole('municipality') || this.isAdmin || this.isUserCpo) && !this.stakeholder && this.hasStatusToStartProcess
    },
    canSelectTenant () {
      return !this.showInfoAlert && this.selectableCpoTenants?.length > 1 && !!this.selectedCpo
    },
    workflowRoles () {
      return this.$auth.user['https://evmaps.nl/workflow'].roles
    },
    isCategorySelectDisabled () {
      return !this.selectedTenant
    },
    canSelectCategories () {
      if (!this.selectableCpos.length) return false

      return (this.hasCpoAsTenant || this.hasRoleAndNoStakeholder) &&
             this.hasStatusToStartProcess &&
             this.isCorrectCpoSelected
    },
    hasStatusToStartProcess () {
      return ['suggestion', 'definitive'].includes(this.chargingpoint.properties.status)
    },
    userIdentity () {
      return this.$auth.user['https://evmaps.nl/identity']?.uuid
    },
    workflowCpoTenant () {
      if (this.stakeholder && this.isCorrectCpoSelected) {
        const cpo = this.getAllOptionsCPO.find(cpo => cpo.uuid === this.stakeholder.uuid)
        return this.selectedCpoTenant || cpo?.tenants?.[0] || ''
      }
      return this.getAllOptionsCPO.find(cpo => cpo.uuid === this.userIdentity)?.tenants?.[0]
    },
    categoryName () {
      return this.processCategories?.find(category => category.value === this.processCategory)?.text
    },
    isRoleAllowed () {
      if (this.getCurrentUserRole === 'guest') return false
      const isMunicipality = this.isAllowedByRole(this.allowedMunicipalityRoles)
      const isCpo = this.workflowCpoTenant && this.isAllowedByRole(this.allowedCpoRoles)

      return !!this.isCorrectCpoSelected || isMunicipality || isCpo || true
    },
    showInfoAlert () {
      return !this.isLoading && (!this.hasStatusToStartProcess || !this.isCorrectCpoSelected || !this.selectableCpos.length)
    },
    remark () {
      return this.chargingpoint.properties.remark
    },
    stakeholder () {
      return this.chargingpoint.properties.stakeholders?.find(s => s.category === 'cpo')
    },

    selectableCpoTenants () {
      const activeTenants = this.activeTenants.map(tenant => ({ value: tenant.tenant, text: tenant.tenantName }))
      return activeTenants.filter(t => this.selectedCpo?.tenants.includes(t.value))
    },
    hasCpoAsTenant () {
      return this.stakeholder && this.getStakeholder.cpos.some(cpo => cpo.uuid === this.stakeholder.uuid)
    },
    isAdmin () {
      return ['superuser', 'admin'].includes(this.currentUserRole)
    },
    isUserCpo () {
      return this.currentUserRole === 'cpo'
    },
    differentUserCpo () {
      if (this.isUserCpo) {
        if (!this.userIdentity || this.stakeholder && this.stakeholder.uuid !== this.userIdentity) {
          return true
        }
      }
      return false
    },
    isCorrectCpoSelected () {
      if (this.differentUserCpo || !this.selectableCpos.length) return false

      return (!!this.stakeholder && this.hasCpoAsTenant) || this.hasRoleAndNoStakeholder
    },
    hasRoleAndNoStakeholder () {
      return (this.isUserCpo || this.isAdmin || this.hasUserRole('municipality')) && !this.stakeholder
    },
  },
  watch: {
    chargingpoint: {
      immediate: true,
      deep: true,
      handler () {
        if (!this.getStakeholder) {
          this.municipalityName = this.chargingpoint.address.municipality
          this.isFetching = false
          return
        }

        if (this.isRoleAllowed) {
          this.selectedCpo = null
          this.selectedCpoTenant = this.selectedTenant = null
          this.processCategory = null
          this.processCategories = null

          this.$nextTick(async () => {
            const allCpoTenants = new Set()
            this.getStakeholder.cpos.map(cpo => cpo.tenants.filter(t => t !== 'go-ral').forEach(t => allCpoTenants.add(t)))
            this.activeTenants = await this.fetchWorkflowTenant(Array.from(allCpoTenants))

            this.selectableTenants = this.activeTenants.map(tenant => ({ value: tenant.tenant, text: tenant.tenantName }))
            this.activeCpos = this.getStakeholder.cpos.filter(cpo => {
              if (this.userIdentity && this.isUserCpo) {
                return cpo.uuid === this.userIdentity
              }
              return this.activeTenants.some(t => cpo.tenants.includes(t.tenant))
            })
            this.selectableCpos = this.activeCpos.map(cpo => ({ value: cpo, text: cpo.name }))

            if (this.stakeholder && this.isCorrectCpoSelected) {
              this.selectedCpo = this.activeCpos.find(cpo => cpo.uuid === this.stakeholder.uuid)
            } else if (this.activeCpos.length === 1) {
              this.selectedCpo = this.activeCpos[0]
            }

            this.isFetching = false
          })
        }
      },
    },
    selectedCpoTenant: {
      handler (tenant) {
        if (this.selectedTenant === tenant) return

        this.processCategory = null
        const selectedTenant = this.activeTenants.find(t => (tenant?.tenant || tenant) === t.tenant)
        this.selectedTenant = tenant
        this.tenantName = selectedTenant?.tenantName
        this.processCategories = selectedTenant?.processCategories
      },
    },
    selectedCpo: {
      async handler (cpo) {

        this.processCategory = this.selectedTenant = this.tenantName = this.selectedCpoTenant = null
        if (!cpo?.tenants) return

        this.isMultiTenantCpo = this.selectableCpoTenants?.length > 1

        if (this.isMultiTenantCpo) {
          this.selectableTenants = this.selectableCpoTenants
        } else {
          this.selectedCpoTenant = this.activeTenants.find(tenant => cpo.tenants.includes(tenant.tenant))
          this.selectedTenant = this.selectedCpoTenant.tenant
          this.processCategories = this.selectedCpoTenant?.processCategories
          this.tenantName = this.selectedCpoTenant.tenantName
        }
      },
    },
  },
  methods: {
    async startWorkflowProcess () {
      this.isLoading = true
      let updatedCp = null

      if (!this.stakeholder) {
        updatedCp = await this.updateChargingPoint()
        if (!updatedCp) {
          return this.isLoading = false
        }
      }

      const tenant = this.selectedTenant?.tenant || this.selectedTenant
      const api = await fetch(`${process.env.VUE_APP_AWS_APIV3_BASE_URL}/process/realisation/`, {
        method: 'POST',
        headers: {
          'x-api-key': process.env.VUE_APP_EVTOOLS_APIKEY,
        },
        body: JSON.stringify({
          tenant,
          chargingpointUuid: this.chargingpoint.uuid,
          category: this.processCategory,
          remark: this.remark,
          user: {
            name: `${this.$auth.user.name} (via Maps)`,
            id: this.$auth.user.sub || 'Unknown',
          },
        }),
      }).catch(err => console.log(err))

      if (api?.ok) {
        this.isLoading = false
        const response = await api.json()
        const error = !response
        if (error) {
          const { message } = JSON.parse(response.body)
          return this.$emit('setAlert', { type: 'danger', message })
        }

        const chargingpoint = this.chargingpoint
        chargingpoint.workflowUuid = response.data.uuid
        chargingpoint.workflowCaseRef = response.data.case?.full
        if (updatedCp) {
          chargingpoint.properties.stakeholders = updatedCp.properties.stakeholders
        }

        this.$store.dispatch('planmode/addOrUpdateChargingPoint', { chargingpoint })
        this.isLoading = false
        this.$emit('setAlert', { type: 'success' })
      }
    },
    isAllowedByRole (roles) {
      return roles.includes(this.workflowRole.role) &&
             ['*', this.getActiveMunicipality].some(code => this.workflowRole.value.includes(code))
    },
    async fetchWorkflowTenant (tenants) {
      const token = await this.$auth.getTokenSilently()
      const api = await fetch('/api/get-workflow-tenant-config', {
        method: 'POST',
        headers: {
          authorization: 'Bearer ' + token,
        },
        body: JSON.stringify({ tenants, activeMunicipality: this.getActiveMunicipality }),
      })

      if (!api.ok) {
        console.log('Could\'t get Workflow tenant municipalities.')
        return
      }
      const response = await api.json()

      return response
    },
    hasUserRole (role) {
      return this.getCurrentUserRole === role
    },
    async updateChargingPoint () {
      this.isLoading = true
      const token = await this.$auth.getTokenSilently()
      const uuid = this.chargingpoint.uuid
      const data = {
        $set: {
          'properties.stakeholders': [this.selectedCpo] || [],
        },
      }
      const api = await fetch('/api/update-chargingpoint', {
        method: 'POST',
        headers: {
          authorization: 'Bearer ' + token,
        },
        body: JSON.stringify({ uuid, data }),
      })

      if (!api.ok) {
        console.log('Could\'t update charging point.')
        return
      }

      return await api.json()
    },
  },
}
</script>
<style lang="scss" scoped>
.create-process {
  position: relative;
  .spinner-border {
    position: absolute;
    left: 1em;
    top: 0.6em;
  }
  &:disabled {
    background: #999 !important;
    border-color: #eee !important;
  }
}
</style>
